import moment from 'moment';
import { isEmpty, isNil } from 'lodash';
import DOMPurify from 'dompurify';

import {
  TimeAMPMEnum,
  SplitTypeEnum,
  AssignmentEditUserResponseModel,
  AssignmentPrimaryDocumentEditModel,
  AnticipatedScheduleTypeEnum,
  AssignmentStatusTypeEnum,
  EmploymentTypeEnum,
  AssignmentTypeEnum,
  JobCategoryEnum,
  AssignmentRateTypeEnum,
  AssignmentTeammateTypeEnum,
  AssignmentCategoryEnum,
  AssignmentTemplateResponseModel,
  PayCodeModel,
  MultiRateSchedulerEditModel,
  AssignmentMultiRateEditModel,
} from 'api/backofficeApiClient';
import {
  PartnerSplitEnum,
  isDraftOrCompletedAssignment,
  getTeammateNameForInitialValues,
} from 'utils/assignmentUtils';
import { ExtraItemEnum } from 'enums';
import { areObjectValuesPresent } from 'utils/objectUtils';
import { AssignmentInitialValues } from 'components/modals/AssignmentDataModal/AssignmentModal';
import { WorkerInfoFormValues as W2WorkerInfoFormValues } from 'components/modals/AssignmentDataModal/W2Assignments/WorkerInfo';
import { WorkerInfoFormValues as ICWorkerInfoFormValues } from 'components/modals/AssignmentDataModal/ICAssignments/WorkerInfo';
import { WorkerInfoFormValues as SCWorkerInfoFormValues } from 'components/modals/AssignmentDataModal/SCAssignments/WorkerInfo';
import { CustomerInfoFormValues } from 'components/modals/AssignmentDataModal/CustomerInfo';
import { WorksiteLocationFormValues } from 'components/modals/AssignmentDataModal/WorksiteLocation';
import { AssignmentInfoFormValues } from 'components/modals/AssignmentDataModal/AssignmentInfo';
import {
  FinancialInfoFormValues,
  MultiRateFormValues,
  PayCodeInfoFormValues,
} from 'components/modals/AssignmentDataModal/FinancialInfo';
import { SplitInfoFormValues } from 'components/modals/AssignmentDataModal/SplitInfo';
import { ContactInfoFormValues } from 'components/modals/AssignmentDataModal/ContactInfo';
import {
  AssignmentEditAdminResponseModel,
  PayCodesGroup,
} from 'api/adminApiClient';
import {
  FinancialSchedulerFormValues,
  PayCodeInfoFormScheduleValues,
} from 'components/modals/FinancialInfoScheduleModal';
import { countryUSAid } from 'constants/index';
import { TimesheetApproverFormValues } from 'components/modals/AssignmentDataModal/CustomerInfo/TimesheetApproverSection';
import { AssignmentPayCodeDetails } from 'components/modals/AssignmentDataModal/interface';
import { getPTOMultiRateDetails } from 'utils/multiRates/multiRatesUtils';
import { updateMultiRateSchedulersWithBilledIndicators } from 'components/modals/AssignmentDataModal/mapUtils';

interface FilteredPayCodesParams {
  payCodesDetails?: AssignmentPayCodeDetails[];
  assignmentType?: AssignmentTypeEnum | null;
  additionalPayCodeIds?: string[];
  payCodesDetailsInfo: PayCodeModel[];
}
const getFilteredPayCodes = ({
  payCodesDetails = [],
  additionalPayCodeIds,
  assignmentType,
  payCodesDetailsInfo,
}: FilteredPayCodesParams): PayCodeModel[] => {
  const payCodesList =
    payCodesDetails.find(
      (typePayCode) => typePayCode.assignmentType === assignmentType,
    )?.payCodeDetails ?? [];

  if (!additionalPayCodeIds || additionalPayCodeIds.length === 0) {
    return payCodesList;
  }

  const existingPayCodeIds = new Set(payCodesList.map((pc) => pc.id));

  const additionalPayCodes = additionalPayCodeIds
    .filter((id) => !existingPayCodeIds.has(id))
    .map((id) => payCodesDetailsInfo?.find((pcd) => pcd.id === id))
    .filter((payCode): payCode is PayCodeModel => payCode != null);

  return [...payCodesList, ...additionalPayCodes];
};

interface FindPayCodesGroupParams {
  payCodesGroups?: PayCodesGroup[];
  jobCategory?: JobCategoryEnum | null;
}
const findPayCodesGroupByJobCategory = ({
  payCodesGroups,
  jobCategory,
}: FindPayCodesGroupParams): string[] | undefined => {
  return payCodesGroups?.find(
    (payCodesGroup) => payCodesGroup.jobCategory === jobCategory,
  )?.payCodes;
};

type PayCodeFilterCriteria = Pick<
  FilteredPayCodesParams,
  'payCodesDetails' | 'assignmentType' | 'payCodesDetailsInfo'
> &
  Pick<FindPayCodesGroupParams, 'jobCategory' | 'payCodesGroups'>;
export const getFilteredPayCodesByJobCriteria = ({
  payCodesDetails,
  assignmentType,
  jobCategory,
  payCodesGroups,
  payCodesDetailsInfo,
}: PayCodeFilterCriteria): PayCodeModel[] => {
  const additionalPayCodeIds = findPayCodesGroupByJobCategory({
    payCodesGroups,
    jobCategory,
  });
  return getFilteredPayCodes({
    payCodesDetails,
    assignmentType,
    additionalPayCodeIds,
    payCodesDetailsInfo,
  });
};

// draft or regular
export const mapW2AssignmentDetailsToInitialValues = ({
  details,
  payCodesDetails,
  isRehireOrRenew,
  isRehireWithNewCustomer,
  payCodesGroups,
  payCodesDetailsInfo,
}: AssignmentInitialValuesParams): AssignmentInitialValues => {
  const { assignmentDocument, assignmentType } = details;

  const payCodes = getFilteredPayCodesByJobCriteria({
    payCodesDetails,
    assignmentType,
    jobCategory: details.assignmentDocument?.assignmentInfo?.jobCategory,
    payCodesGroups,
    payCodesDetailsInfo,
  });

  return {
    workerInfo: mapWorkerInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    customerInfo: mapCustomerInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      isRehireWithNewCustomer,
    ),
    worksiteLocation: mapWorksiteLocationToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    assignmentInfo: mapAssignmentInfoToInitialValues(details, isRehireOrRenew),
    financialInfo: mapFinancialInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
    splitInfo: mapSplitInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    template: {
      templateName:
        (details as AssignmentTemplateResponseModel)?.templateName || '',
      isNeedCreateTemplate: false,
    },
    financialScheduler: mapFinancialSchedulerToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
  };
};

type AssignmentInitialValuesParams = {
  details: AssignmentEditUserResponseModel | AssignmentTemplateResponseModel;
  isRehireOrRenew?: boolean;
  isRehireWithNewCustomer?: boolean;
} & Pick<FindPayCodesGroupParams, 'payCodesGroups'> &
  Pick<FilteredPayCodesParams, 'payCodesDetails'> &
  Pick<PayCodeFilterCriteria, 'payCodesDetailsInfo'>;

// draft or regular
export const mapW2AssignmentDetailsToCorporateInitialValues = ({
  details,
  payCodesDetails,
  payCodesGroups,
  isRehireOrRenew,
  payCodesDetailsInfo,
}: AssignmentInitialValuesParams): AssignmentInitialValues => {
  const { assignmentDocument, assignmentType } = details;

  const payCodes = getFilteredPayCodesByJobCriteria({
    payCodesDetails,
    assignmentType,
    jobCategory: assignmentDocument?.assignmentInfo?.jobCategory,
    payCodesGroups,
    payCodesDetailsInfo,
  });

  return {
    workerInfo: mapWorkerInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    customerInfo: mapContactInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      (details as AssignmentEditUserResponseModel)
        ?.assignmentStatus as AssignmentStatusTypeEnum,
    ),
    worksiteLocation: mapWorksiteLocationToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    assignmentInfo: mapAssignmentInfoToInitialValues(details, isRehireOrRenew),
    financialInfo: mapFinancialInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
    splitInfo: mapSplitInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    template: {
      templateName:
        (details as AssignmentTemplateResponseModel)?.templateName || '',
      isNeedCreateTemplate: false,
    },
    financialScheduler: mapFinancialSchedulerToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
  };
};

// draft or regular
export const mapICAssignmentDetailsToInitialValues = ({
  details,
  payCodesDetails,
  isRehireOrRenew,
  isRehireWithNewCustomer,
  payCodesGroups,
  payCodesDetailsInfo,
}: AssignmentInitialValuesParams): AssignmentInitialValues => {
  const { assignmentDocument, assignmentType } = details;

  const payCodes = getFilteredPayCodesByJobCriteria({
    payCodesDetails,
    assignmentType,
    jobCategory: assignmentDocument?.assignmentInfo?.jobCategory,
    payCodesGroups,
    payCodesDetailsInfo,
  });

  return {
    workerInfo: mapWorkerInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    customerInfo: mapCustomerInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      isRehireWithNewCustomer,
    ),
    worksiteLocation: mapWorksiteLocationToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    assignmentInfo: mapAssignmentInfoToInitialValues(details, isRehireOrRenew),
    financialInfo: mapFinancialInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
    splitInfo: mapSplitInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    template: {
      templateName:
        (details as AssignmentTemplateResponseModel)?.templateName || '',
      isNeedCreateTemplate: false,
    },
    financialScheduler: mapFinancialSchedulerToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
  };
};

// draft or regular
export const mapICAssignmentDetailsToCorporateInitialValues = ({
  details,
  payCodesDetails,
  isRehireOrRenew,
  payCodesGroups,
  payCodesDetailsInfo,
}: AssignmentInitialValuesParams): AssignmentInitialValues => {
  const { assignmentDocument, assignmentType } = details;

  const payCodes = getFilteredPayCodesByJobCriteria({
    payCodesDetails,
    assignmentType,
    jobCategory: assignmentDocument?.assignmentInfo?.jobCategory,
    payCodesGroups,
    payCodesDetailsInfo,
  });

  return {
    workerInfo: mapWorkerInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    customerInfo: mapContactInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      (details as AssignmentEditUserResponseModel)
        ?.assignmentStatus as AssignmentStatusTypeEnum,
    ),
    worksiteLocation: mapWorksiteLocationToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    assignmentInfo: mapAssignmentInfoToInitialValues(details, isRehireOrRenew),
    financialInfo: mapFinancialInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
    splitInfo: mapSplitInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    template: {
      templateName:
        (details as AssignmentTemplateResponseModel)?.templateName || '',
      isNeedCreateTemplate: false,
    },
    financialScheduler: mapFinancialSchedulerToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
  };
};

// draft or regular
export const mapSCAssignmentDetailsToInitialValues = ({
  details,
  payCodesDetails,
  isRehireOrRenew,
  isRehireWithNewCustomer,
  payCodesGroups,
  payCodesDetailsInfo,
}: AssignmentInitialValuesParams): AssignmentInitialValues => {
  const { assignmentDocument, assignmentType } = details;

  const payCodes = getFilteredPayCodesByJobCriteria({
    payCodesDetails,
    assignmentType,
    jobCategory: assignmentDocument?.assignmentInfo?.jobCategory,
    payCodesGroups,
    payCodesDetailsInfo,
  });

  return {
    workerInfo: mapWorkerInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    customerInfo: mapCustomerInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      isRehireWithNewCustomer,
    ),
    worksiteLocation: mapWorksiteLocationToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    assignmentInfo: mapAssignmentInfoToInitialValues(details, isRehireOrRenew),
    financialInfo: mapFinancialInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
    splitInfo: mapSplitInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    template: {
      templateName:
        (details as AssignmentTemplateResponseModel)?.templateName || '',
      isNeedCreateTemplate: false,
    },
    financialScheduler: mapFinancialSchedulerToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
  };
};

// draft or regular
export const mapSCAssignmentDetailsToCorporateInitialValues = ({
  details,
  payCodesDetails,
  isRehireOrRenew,
  payCodesGroups,
  payCodesDetailsInfo,
}: AssignmentInitialValuesParams): AssignmentInitialValues => {
  const { assignmentDocument, assignmentType } = details;

  const payCodes = getFilteredPayCodesByJobCriteria({
    payCodesDetails,
    assignmentType,
    jobCategory: assignmentDocument?.assignmentInfo?.jobCategory,
    payCodesGroups,
    payCodesDetailsInfo,
  });

  return {
    workerInfo: mapWorkerInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    customerInfo: mapContactInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      (details as AssignmentEditUserResponseModel)
        ?.assignmentStatus as AssignmentStatusTypeEnum,
    ),
    worksiteLocation: mapWorksiteLocationToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    assignmentInfo: mapAssignmentInfoToInitialValues(details, isRehireOrRenew),
    financialInfo: mapFinancialInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
    splitInfo: mapSplitInfoToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
    ),
    template: {
      templateName:
        (details as AssignmentTemplateResponseModel)?.templateName || '',
      isNeedCreateTemplate: false,
    },
    financialScheduler: mapFinancialSchedulerToInitialValues(
      assignmentDocument as AssignmentPrimaryDocumentEditModel,
      payCodes,
    ),
  };
};

const mapCustomerInfoToInitialValues = (
  assignment: AssignmentPrimaryDocumentEditModel,
  isRehireWithNewCustomer?: boolean,
): CustomerInfoFormValues => {
  const {
    customerId,
    bestPrimaryContacts,
    bestBillingContacts,
    supervisorInfo,
    additionalTimesheetApprovers = [],
    timesheetApproverInfo,
    timesheetBackupApproverInfo,
    timesheetApproverNotApplicable,
    timesheetApproverSameAsSupervisor,
  } = assignment;

  const timesheetApprovers = [
    timesheetApproverInfo,
    ...additionalTimesheetApprovers,
  ];
  const mappedTimesheetApprovers = timesheetApprovers.map(
    (timesheetApprover) => ({
      firstName: timesheetApprover?.firstName || '',
      lastName: timesheetApprover?.lastName || '',
      email: timesheetApprover?.email || '',
      phone: timesheetApprover?.phoneNumber || '',
    }),
  );

  const otherBillingContact = bestBillingContacts?.filter(
    (contact) => !contact.contactId,
  );

  const billingContacts =
    bestBillingContacts
      ?.filter((contact) => contact.contactId)
      .map((contact) => contact.contactId as string) || [];

  if (otherBillingContact?.length) {
    billingContacts.push(ExtraItemEnum.AddBillingContact);
  }

  const otherPrimaryContact = bestPrimaryContacts?.find(
    (contact) => !contact.contactId,
  );

  const bestContacts =
    bestPrimaryContacts
      ?.filter((contact) => contact.contactId)
      .map((contact) => contact.contactId as string) || [];

  if (otherPrimaryContact) {
    bestContacts.push(ExtraItemEnum.Other);
  }

  const initialPrimaryContacts = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
  };

  const initialSupervisorInfo = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
  };

  const initialTimeBackupApprover = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  };

  const initialTimesheetApprover: TimesheetApproverFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  };

  return {
    generalInfo: {
      customer: !isRehireWithNewCustomer ? customerId || '' : '',
      bestContacts: !isRehireWithNewCustomer ? bestContacts : [],
      billingContacts: !isRehireWithNewCustomer ? billingContacts : [],
      newBillingContacts:
        !isRehireWithNewCustomer && otherBillingContact?.length
          ? otherBillingContact
          : [],
      primaryContacts: !isRehireWithNewCustomer
        ? {
            firstName: otherPrimaryContact?.firstName || '',
            lastName: otherPrimaryContact?.lastName || '',
            email: otherPrimaryContact?.email || '',
            phone: otherPrimaryContact?.phoneNumber || '',
            title: otherPrimaryContact?.title || '',
          }
        : initialPrimaryContacts,
    },

    supervisorInfo: !isRehireWithNewCustomer
      ? {
          firstName: supervisorInfo?.firstName || '',
          lastName: supervisorInfo?.lastName || '',
          title: supervisorInfo?.title || '',
          email: supervisorInfo?.email || '',
          phone: supervisorInfo?.phoneNumber || '',
        }
      : initialSupervisorInfo,

    timesheetApproverInfo: {
      sameAsSupervisor: !isRehireWithNewCustomer
        ? timesheetApproverSameAsSupervisor || false
        : false,
      notApplicable: !isRehireWithNewCustomer
        ? timesheetApproverNotApplicable || false
        : false,
      timesheetApprovers: !isRehireWithNewCustomer
        ? mappedTimesheetApprovers
        : [initialTimesheetApprover],
    },

    timeBackupApprover: !isRehireWithNewCustomer
      ? {
          firstName: timesheetBackupApproverInfo?.firstName || '',
          lastName: timesheetBackupApproverInfo?.lastName || '',
          email: timesheetBackupApproverInfo?.email || '',
          phone: timesheetBackupApproverInfo?.phoneNumber || '',
        }
      : initialTimeBackupApprover,
  };
};

const mapContactInfoToInitialValues = (
  assignment: AssignmentPrimaryDocumentEditModel,
  status: AssignmentStatusTypeEnum,
): ContactInfoFormValues => {
  const {
    bestPrimaryContacts,
    supervisorInfo,
    timesheetApproverInfo,
    timesheetBackupApproverInfo,
    timesheetApproverSameAsSupervisor,
    timesheetApproverNotApplicable,
  } = assignment;
  // Hide selected contacts for draft/completed assignment
  const isDraftOrCompleted = isDraftOrCompletedAssignment(status);

  const otherPrimaryContact = !isDraftOrCompleted
    ? bestPrimaryContacts?.find((contact) => !contact.contactId)
    : undefined;

  const bestContacts = !isDraftOrCompleted
    ? bestPrimaryContacts
        ?.filter((contact) => contact.contactId)
        .map((contact) => contact.contactId as string) || []
    : [];

  if (otherPrimaryContact) {
    bestContacts.push(ExtraItemEnum.Other);
  }

  const isOtherSupervisor =
    !supervisorInfo?.supervisorId &&
    [
      supervisorInfo?.title,
      supervisorInfo?.firstName,
      supervisorInfo?.lastName,
      supervisorInfo?.email,
      supervisorInfo?.phoneNumber,
    ].some((data) => !isNil(data));

  const isOtherTimesheetApprover =
    !timesheetApproverInfo?.timesheetApproverId &&
    [
      timesheetApproverInfo?.firstName,
      timesheetApproverInfo?.lastName,
      timesheetApproverInfo?.email,
      timesheetApproverInfo?.phoneNumber,
    ].some((data) => !isNil(data));

  return {
    primaryContacts: {
      bestContacts,
      newPrimaryContact: {
        firstName: otherPrimaryContact?.firstName || '',
        lastName: otherPrimaryContact?.lastName || '',
        title: otherPrimaryContact?.title || '',
        email: otherPrimaryContact?.email || '',
        phone: otherPrimaryContact?.phoneNumber || '',
      },
    },

    supervisorInfo: {
      supervisor: isDraftOrCompleted
        ? ''
        : !isOtherSupervisor
          ? supervisorInfo?.supervisorId || ''
          : ExtraItemEnum.Other,
      newSupervisor: {
        firstName: !isDraftOrCompleted ? supervisorInfo?.firstName || '' : '',
        lastName: !isDraftOrCompleted ? supervisorInfo?.lastName || '' : '',
        title: !isDraftOrCompleted ? supervisorInfo?.title || '' : '',
        email: !isDraftOrCompleted ? supervisorInfo?.email || '' : '',
        phone:
          !isDraftOrCompleted && supervisorInfo?.phoneNumber
            ? supervisorInfo?.phoneNumber
            : '',
      },
    },

    timesheetApprover: {
      sameAsSupervisor: timesheetApproverSameAsSupervisor || false,
      approver: isDraftOrCompleted
        ? ''
        : !isOtherTimesheetApprover
          ? timesheetApproverInfo?.timesheetApproverId || ''
          : ExtraItemEnum.Other,
      newTimesheetApprover: {
        firstName: !isDraftOrCompleted
          ? timesheetApproverInfo?.firstName || ''
          : '',
        lastName: !isDraftOrCompleted
          ? timesheetApproverInfo?.lastName || ''
          : '',
        email: !isDraftOrCompleted ? timesheetApproverInfo?.email || '' : '',
        phone:
          !isDraftOrCompleted && timesheetApproverInfo?.phoneNumber
            ? timesheetApproverInfo?.phoneNumber
            : '',
      },
      notApplicable: timesheetApproverNotApplicable || false,
    },

    timeBackupApprover: {
      firstName: timesheetBackupApproverInfo?.firstName || '',
      lastName: timesheetBackupApproverInfo?.lastName || '',
      email: timesheetBackupApproverInfo?.email || '',
      phone: timesheetBackupApproverInfo?.phoneNumber
        ? timesheetBackupApproverInfo?.phoneNumber
        : '',
    },
  };
};

export const mapWorksiteLocationToInitialValues = (
  assignment: AssignmentPrimaryDocumentEditModel,
): WorksiteLocationFormValues => {
  const {
    workLocation,
    billingLocation,
    workSameAsHomeOrBusinessLocation,
    billingSameAsWorkLocation,
  } = assignment;

  return {
    work: {
      country: workLocation?.countryId || countryUSAid,
      locationSameAsMain: workSameAsHomeOrBusinessLocation || false,
      state: workLocation?.stateId || undefined,
      internationalStateName: workLocation?.internationalStateName || '',
      city: workLocation?.city || '',
      internationalCityName: workLocation?.internationalCityName || '',
      address: workLocation?.address || '',
      zip: workLocation?.zipCode || '',
    },
    billing: {
      country: billingLocation?.countryId || countryUSAid,
      locationSameAsWork: billingSameAsWorkLocation || false,
      state: billingLocation?.stateId || undefined,
      internationalStateName: billingLocation?.internationalStateName || '',
      city: billingLocation?.city || '',
      internationalCityName: billingLocation?.internationalCityName || '',
      address: billingLocation?.address || '',
      zip: billingLocation?.zipCode || '',
    },
  };
};

const mapAssignmentInfoToInitialValues = (
  details: AssignmentEditUserResponseModel,
  isRehireOrRenew?: boolean,
): AssignmentInfoFormValues => {
  const { assignmentInfo } =
    details.assignmentDocument as AssignmentPrimaryDocumentEditModel;

  let anticipateOtherData: any = {
    hoursFrom: null,
    dayTimeFrom: TimeAMPMEnum.AM,
    hoursTo: null,
    dayTimeTo: TimeAMPMEnum.PM,
  };

  if (
    assignmentInfo?.anticipatedScheduleType ===
    AnticipatedScheduleTypeEnum.Other
  ) {
    anticipateOtherData = {
      hoursFrom: assignmentInfo?.anticipatedSchedule?.fromTime || null,
      dayTimeFrom:
        assignmentInfo?.anticipatedSchedule?.fromAMPM || TimeAMPMEnum.AM,
      hoursTo: assignmentInfo?.anticipatedSchedule?.toTime || null,
      dayTimeTo: assignmentInfo?.anticipatedSchedule?.toAMPM || TimeAMPMEnum.PM,
    };
  }

  return {
    jobTitle: assignmentInfo?.jobTitle || '',
    startDate:
      !isRehireOrRenew && assignmentInfo?.startDate
        ? moment
            .utc(assignmentInfo?.startDate)
            .format('MM/DD/YYYY')
            .toString()
        : null,
    endDate:
      !isRehireOrRenew && assignmentInfo?.endDate
        ? moment
            .utc(assignmentInfo?.endDate)
            .format('MM/DD/YYYY')
            .toString()
        : null,
    jobDescription: {
      content: assignmentInfo?.jobDescription
        ? DOMPurify.sanitize(assignmentInfo.jobDescription)
        : '',
    },
    jobCategory: !isNil(assignmentInfo?.jobCategory)
      ? (assignmentInfo?.jobCategory as JobCategoryEnum)
      : null,
    employmentType: !isNil(assignmentInfo?.employmentType)
      ? (assignmentInfo?.employmentType as EmploymentTypeEnum)
      : null,
    anticipateSchedule: !isNil(assignmentInfo?.anticipatedScheduleType)
      ? (assignmentInfo?.anticipatedScheduleType as AnticipatedScheduleTypeEnum)
      : null,
    customerPurchaseOrder: !isNil(assignmentInfo?.customerPurchaseOrder)
      ? assignmentInfo?.customerPurchaseOrder
      : undefined,
    anticipateOtherData,
    workFromHome: assignmentInfo?.workFromHome,
    preScreeningBilledTo: assignmentInfo?.preScreeningBilledTo || null,
    preScreeningRequired: assignmentInfo?.preScreeningRequired || false,
    specialInstructions: assignmentInfo?.specialInstructions || '',
    internalReports: details.assignmentTeammates?.length
      ? details.assignmentTeammates.map((teammate) => ({
          userId: getTeammateNameForInitialValues(teammate),
          externalFullName: teammate.externalFullName || undefined,
          teammateType: teammate.teammateType,
          rate: teammate.rate || undefined,
          rateType: teammate.rateType || AssignmentRateTypeEnum.Percent,
        }))
      : [
          {
            userId: '',
            rate: undefined,
            teammateType: AssignmentTeammateTypeEnum.Teammate,
            externalFullName: undefined,
            rateType: AssignmentRateTypeEnum.Percent,
          },
        ],
    supportingDocuments: (assignmentInfo?.attachments || []).map(
      (attachment) => ({
        fileName: attachment.fileName || '',
        s3Key: attachment.fileKey || '',
      }),
    ),
    // workCode: assignmentInfo?.workCode || undefined,
  };
};

const mapFinancialSchedulerToInitialValues = (
  assignment: AssignmentPrimaryDocumentEditModel,
  payCodes: PayCodeModel[],
): FinancialSchedulerFormValues | undefined => {
  if (!assignment.financialScheduler) return;

  const {
    financialInfoScheduler,
    multiRateSchedulers = [],
    effectiveDate,
  } = assignment.financialScheduler;

  const updatedRateSchedules = updateMultiRateSchedulersWithBilledIndicators(
    assignment?.multiRates,
    multiRateSchedulers,
  );

  const ptoMultiRateDetails = getPTOMultiRateDetails(
    payCodes,
    assignment?.multiRates,
  );

  const grouped = (updatedRateSchedules || []).reduce<{
    [key: string]: PayCodeInfoFormScheduleValues[];
  }>((acc, item) => {
    const payCode = payCodes.find((code) => code.id === item.payCodeId);

    if (!payCode) return acc;

    const payRateTypeId = payCode.payRateTypeId as string;

    if (!acc[payRateTypeId]) {
      acc[payRateTypeId] = [];
    }

    let obj: PayCodeInfoFormScheduleValues = {
      payCodeId: item.payCodeId,
      hasBillRateCustomLogic: item?.hasBillRateCustomLogic || false,
      hasPayRateCustomLogic: item?.hasPayRateCustomLogic || false,
      memberBilledIndicator: item?.memberBilledIndicator,
    };

    if (obj.payCodeId === ptoMultiRateDetails?.payCodeId) {
      obj = {
        ...obj,
        memberBilledIndicator: ptoMultiRateDetails?.memberBilledIndicator,
      };
    }

    if (!isNil(item.payRate)) {
      obj = { ...obj, payRate: item.payRate };
    }

    if (!isNil(item.billRate)) {
      obj = { ...obj, billRate: item.billRate };
    }

    acc[payRateTypeId].push(obj);

    return acc;
  }, {});

  const mappedMultiRates: MultiRateFormValues[] = Object.entries(grouped).map(
    ([payTypeId, payCodesInfo]) => {
      return {
        id: payTypeId,
        activePayCodeId: (payCodesInfo as PayCodeInfoFormValues[])?.[0]
          ?.payCodeId,
        payTypeId: payTypeId,
        payCodesInfo: payCodesInfo as PayCodeInfoFormValues[],
      };
    },
  );
  return {
    financialInfoScheduler,
    multiRates: mappedMultiRates,
    effectiveDate: effectiveDate,
  };
};

interface GroupedRates {
  [payRateTypeId: string]: PayCodeInfoFormValues[];
}

const mapFinancialInfoToInitialValues = (
  assignment: AssignmentPrimaryDocumentEditModel,
  payCodes: PayCodeModel[],
): FinancialInfoFormValues => {
  const { financialInfo, multiRates = [], financialScheduler } = assignment;

  const multiRateSchedulers: MultiRateSchedulerEditModel[] =
    financialScheduler?.multiRateSchedulers || [];
  const filteredSchedulers: MultiRateSchedulerEditModel[] = multiRateSchedulers
    .filter((multiRateScheduler) => {
      return !multiRates.some(
        (multiRate) => multiRate.payCodeId === multiRateScheduler.payCodeId,
      );
    })
    .map((multiRate) => ({
      payCodeId: multiRate?.payCodeId,
      billRate: !isNil(multiRate?.billRate) ? multiRate?.billRate : undefined,
      payRate: !isNil(multiRate?.payRate) ? multiRate?.payRate : undefined,
      hasPayRateCustomLogic: multiRate?.hasPayRateCustomLogic || false,
      hasBillRateCustomLogic: multiRate?.hasBillRateCustomLogic || false,
    }));

  const combinedRates: PayCodeInfoFormValues[] = [
    ...multiRates,
    ...filteredSchedulers,
  ].map((multiRate) => ({
    id: (multiRate as AssignmentMultiRateEditModel)?.id || undefined,
    payCodeId: multiRate?.payCodeId,
    payRate: !isNil(multiRate?.payRate) ? multiRate?.payRate : undefined,
    billRate: !isNil(multiRate?.billRate) ? multiRate?.billRate : undefined,
    specialInstructions:
      (multiRate as AssignmentMultiRateEditModel)?.specialInstructions ||
      undefined,
    memberBilledIndicator: (multiRate as AssignmentMultiRateEditModel)
      ?.memberBilledIndicator,
    hasPayRateCustomLogic: multiRate?.hasPayRateCustomLogic || false,
    hasBillRateCustomLogic: multiRate?.hasBillRateCustomLogic || false,
  }));

  const groupedRates = combinedRates.reduce(
    (accumulator: GroupedRates, item: PayCodeInfoFormValues) => {
      const payCode = payCodes.find((code) => code.id === item.payCodeId);
      if (payCode && payCode.payRateTypeId !== undefined) {
        accumulator[payCode.payRateTypeId] =
          accumulator[payCode.payRateTypeId] || [];
        accumulator[payCode.payRateTypeId].push({
          id: item?.id,
          payCodeId: item.payCodeId,
          payRate: !isNil(item.payRate) ? item.payRate : undefined,
          billRate: !isNil(item.billRate) ? item.billRate : undefined,
          specialInstructions: item.specialInstructions ?? undefined,
          memberBilledIndicator: item.memberBilledIndicator,
          hasPayRateCustomLogic: item?.hasPayRateCustomLogic || false,
          hasBillRateCustomLogic: item?.hasBillRateCustomLogic || false,
        });
      }

      return accumulator;
    },
    {},
  );
  const mappedMultiRates: MultiRateFormValues[] = Object.entries(
    groupedRates,
  ).map(([payTypeId, payCodesInfo]) => {
    const payCodesInfoFormValues = payCodes
      .filter(({ payRateTypeId }) => payRateTypeId === payTypeId)
      .map(({ id }) => {
        const payCodeInfo = payCodesInfo.find((p) => p.payCodeId === id);
        return {
          id: payCodeInfo?.id,
          payCodeId: payCodeInfo?.payCodeId || id,
          billRate: !isNil(payCodeInfo?.billRate)
            ? payCodeInfo?.billRate
            : undefined,
          payRate: !isNil(payCodeInfo?.payRate)
            ? payCodeInfo?.payRate
            : undefined,
          specialInstructions: payCodeInfo?.specialInstructions || undefined,
          memberBilledIndicator: payCodeInfo?.memberBilledIndicator,
          hasPayRateCustomLogic: payCodeInfo?.hasPayRateCustomLogic || false,
          hasBillRateCustomLogic: payCodeInfo?.hasBillRateCustomLogic || false,
        };
      });

    return {
      id: payTypeId,
      activePayCodeId: payCodesInfoFormValues[0]?.payCodeId,
      payTypeId,
      payCodesInfo: payCodesInfoFormValues,
    };
  });

  return {
    billRate: financialInfo?.billRate ?? null,
    payRate: financialInfo?.payRate ?? null,
    overtimeBillRate: financialInfo?.overtimeBillRate ?? null,
    overtimePayRate: financialInfo?.overtimePayRate ?? null,
    profitRate: financialInfo?.profitRate ?? null,
    overtime: financialInfo?.overtime ?? null,
    payType: financialInfo?.payType ?? null,
    mbpRate: financialInfo?.mbpRate ?? null,
    dtBillRate: financialInfo?.dtBillRate ?? null,
    dtPayRate: financialInfo?.dtPayRate ?? null,
    multiRates: mappedMultiRates,
  };
};

const mapSplitInfoToInitialValues = (
  assignment: AssignmentPrimaryDocumentEditModel,
): SplitInfoFormValues => {
  const { splitInfo, partnerDraft } = assignment;

  let result: SplitInfoFormValues = {
    splitType: splitInfo?.splitType || SplitTypeEnum.NoSplit,
    existingPartnerData: {
      partner: '',
      splitAmount: null,
      splitLabel: PartnerSplitEnum.SplitPercent,
    },
    newPartnerData: {
      companyName: '',
      splitAmount: null,
      splitLabel: PartnerSplitEnum.SplitPercent,
      subcontractorRate: undefined,
      country: countryUSAid,
      internationalStateName: undefined,
      state: undefined,
      internationalCityName: undefined,
      city: '',
      address: '',
      zip: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  };

  switch (splitInfo?.splitType) {
    case SplitTypeEnum.ExistingPartner:
      result.existingPartnerData = {
        partner: splitInfo?.partnerId || '',
        splitAmount: !isNil(splitInfo?.splitPercent)
          ? splitInfo?.splitPercent
          : splitInfo?.splitAmount || null,
        splitLabel: !isNil(splitInfo?.splitAmount)
          ? PartnerSplitEnum.PaymentPerHour
          : PartnerSplitEnum.SplitPercent,
      };
      break;
    case SplitTypeEnum.NewPartner:
      result.newPartnerData = {
        companyName: partnerDraft?.companyName || '',
        splitAmount: !isNil(splitInfo?.splitPercent)
          ? splitInfo?.splitPercent
          : splitInfo?.splitAmount || null,
        splitLabel: !isNil(splitInfo?.splitAmount)
          ? PartnerSplitEnum.PaymentPerHour
          : PartnerSplitEnum.SplitPercent,
        subcontractorRate: !isNil(partnerDraft?.subcontractorRate)
          ? partnerDraft?.subcontractorRate
          : null,
        country: partnerDraft?.countryId || undefined,
        state: partnerDraft?.stateId || undefined,
        internationalStateName:
          partnerDraft?.internationalStateName || undefined,
        internationalCityName: partnerDraft?.internationalCityName || undefined,
        city: partnerDraft?.city || '',
        address: partnerDraft?.address || '',
        zip: partnerDraft?.zipCode || '',

        firstName: partnerDraft?.firstName || '',
        lastName: partnerDraft?.lastName || '',
        email: partnerDraft?.email || '',
        phone: partnerDraft?.phoneNumber || '',
      };
      break;

    default:
      break;
  }

  return result;
};

type WorkerInfoFormValues = W2WorkerInfoFormValues &
  ICWorkerInfoFormValues &
  SCWorkerInfoFormValues;
export const mapWorkerInfoToInitialValues = (
  assignment: AssignmentPrimaryDocumentEditModel,
): WorkerInfoFormValues => {
  const { assignmentType, workerInfo, contractorCompanyInfo } = assignment;
  const isW2AssignmentType = assignmentType === AssignmentTypeEnum.EEAssignment;
  // Display add company option if company was not specified
  // and any of the fields were filled
  const isAddCompany =
    !contractorCompanyInfo?.companyId &&
    (!isEmpty(contractorCompanyInfo?.companyName) ||
      areObjectValuesPresent(contractorCompanyInfo?.location) ||
      areObjectValuesPresent(contractorCompanyInfo?.workerFile) ||
      areObjectValuesPresent(contractorCompanyInfo?.representativeContact));

  // Display add worker option if worker was not specified
  // and any of the fields were filled
  const isAddWorker =
    !workerInfo?.workerId &&
    (areObjectValuesPresent(workerInfo?.location) ||
      areObjectValuesPresent(workerInfo?.workerFile) ||
      areObjectValuesPresent(workerInfo?.workerInfo));

  let workerInfoValues = {
    worker: {
      workerId: isAddWorker
        ? ExtraItemEnum.AddWorker
        : workerInfo?.workerId || '',
      firstName: workerInfo?.workerInfo?.firstName || '',
      lastName: workerInfo?.workerInfo?.lastName || '',
      email: workerInfo?.workerInfo?.email || '',
      phone: workerInfo?.workerInfo?.phoneNumber
        ? workerInfo?.workerInfo?.phoneNumber
        : '',
      workerType: workerInfo?.workerType,
      country: isAddWorker ? countryUSAid : workerInfo?.location?.countryId,
      internationalStateName:
        workerInfo?.location?.internationalStateName || undefined,
      internationalCityName:
        workerInfo?.location?.internationalCityName || undefined,
      state: workerInfo?.location?.stateId || undefined,
      city: workerInfo?.location?.city || '',
      address: workerInfo?.location?.address || '',
      zip: workerInfo?.location?.zipCode || '',
    },
  } as WorkerInfoFormValues;

  if (!isW2AssignmentType) {
    workerInfoValues = {
      ...workerInfoValues,
      company: {
        companyId: isAddCompany
          ? ExtraItemEnum.AddCompany
          : contractorCompanyInfo?.companyId || '',
        companyName: contractorCompanyInfo?.companyName || '',
        firstName:
          contractorCompanyInfo?.representativeContact?.firstName || '',
        lastName: contractorCompanyInfo?.representativeContact?.lastName || '',
        email: contractorCompanyInfo?.representativeContact?.email || '',
        phone: contractorCompanyInfo?.representativeContact?.phoneNumber
          ? contractorCompanyInfo?.representativeContact?.phoneNumber
          : '',
        country: contractorCompanyInfo?.location?.countryId || countryUSAid,
        state: contractorCompanyInfo?.location?.stateId || undefined,
        internationalStateName:
          contractorCompanyInfo?.location?.internationalStateName || '',
        city: contractorCompanyInfo?.location?.city || '',
        internationalCityName:
          contractorCompanyInfo?.location?.internationalCityName || '',
        address: contractorCompanyInfo?.location?.address || '',
        zip: contractorCompanyInfo?.location?.zipCode || '',
      },
    };

    if (assignmentType === AssignmentTypeEnum.ICAssignment) {
      workerInfoValues.workerFileKey = workerInfo?.workerFile?.fileKey
        ? [workerInfo?.workerFile?.fileKey]
        : [];
      workerInfoValues.taxId = !isNil(workerInfo?.workerInfo?.taxId)
        ? (workerInfo?.workerInfo?.taxId as number)
        : null;
    }

    if (assignmentType === AssignmentTypeEnum.SCAssignment) {
      workerInfoValues.workerFileKey = contractorCompanyInfo?.workerFile
        ?.fileKey
        ? [contractorCompanyInfo.workerFile?.fileKey]
        : [];
      workerInfoValues.taxId = !isNil(
        contractorCompanyInfo?.representativeContact?.taxId,
      )
        ? (contractorCompanyInfo?.representativeContact?.taxId as number)
        : null;
    }
  }

  return workerInfoValues;
};

interface AssignmentEditMappingParams {
  editAssignmentInfo:
    | AssignmentEditUserResponseModel
    | AssignmentEditAdminResponseModel
    | AssignmentTemplateResponseModel;
  payCodesDetails?: AssignmentPayCodeDetails[];
  payCodesGroups?: PayCodesGroup[];
  payCodesDetailsInfo: PayCodeModel[];
}

export const mapAssigmentModelToInitialValues = ({
  editAssignmentInfo,
  payCodesDetailsInfo,
  payCodesGroups,
  payCodesDetails,
}: AssignmentEditMappingParams): AssignmentInitialValues | undefined => {
  const { assignmentCategory, assignmentType } = editAssignmentInfo;
  if (!assignmentType || !assignmentCategory) return;

  let initialValues: AssignmentInitialValues | undefined;

  switch (assignmentType) {
    case AssignmentTypeEnum.EEAssignment: {
      switch (assignmentCategory) {
        case AssignmentCategoryEnum.Corporate: {
          initialValues = mapW2AssignmentDetailsToCorporateInitialValues({
            details: editAssignmentInfo,
            payCodesDetails,
            payCodesGroups,
            payCodesDetailsInfo,
          });
          break;
        }
        case AssignmentCategoryEnum.Recruiter: {
          initialValues = mapW2AssignmentDetailsToInitialValues({
            details: editAssignmentInfo,
            payCodesDetails,
            payCodesGroups,
            payCodesDetailsInfo,
          });
          break;
        }
      }
      break;
    }

    case AssignmentTypeEnum.ICAssignment: {
      switch (assignmentCategory) {
        case AssignmentCategoryEnum.Corporate: {
          initialValues = mapICAssignmentDetailsToCorporateInitialValues({
            details: editAssignmentInfo,
            payCodesDetails,
            payCodesGroups,
            payCodesDetailsInfo,
          });
          break;
        }
        case AssignmentCategoryEnum.Recruiter: {
          initialValues = mapICAssignmentDetailsToInitialValues({
            details: editAssignmentInfo,
            payCodesDetails,
            payCodesGroups,
            payCodesDetailsInfo,
          });
          break;
        }
      }
      break;
    }

    case AssignmentTypeEnum.SCAssignment: {
      switch (assignmentCategory) {
        case AssignmentCategoryEnum.Corporate: {
          initialValues = mapSCAssignmentDetailsToCorporateInitialValues({
            details: editAssignmentInfo,
            payCodesDetails,
            payCodesGroups,
            payCodesDetailsInfo,
          });
          break;
        }
        case AssignmentCategoryEnum.Recruiter: {
          initialValues = mapSCAssignmentDetailsToInitialValues({
            details: editAssignmentInfo,
            payCodesDetails,
            payCodesGroups,
            payCodesDetailsInfo,
          });
          break;
        }
      }
      break;
    }
  }
  return initialValues;
};
